/**
 * An announcement: one line of news under the app's header, written on /admin
 * in every interface language and shown between two moments.
 *
 * The rules live here rather than in the route so the admin form and the Worker
 * refuse the same things, and so a test can hold them.
 */

export const ANNOUNCEMENT_LANGUAGES = ["pl", "uk", "ru", "en"] as const;
export type AnnouncementLanguage = (typeof ANNOUNCEMENT_LANGUAGES)[number];

/** One line in a bar, not an article: longer than this wraps into a wall on a phone. */
export const ANNOUNCEMENT_MAX_CHARS = 200;
/** A button's worth of words. */
export const ANNOUNCEMENT_LABEL_MAX_CHARS = 40;

export interface AnnouncementInput {
  text: Record<AnnouncementLanguage, string>;
  /** An in-app path without language prefix (`/pass`), an https URL, or null. */
  link: string | null;
  /** The link's words in each language, or null for the catalogue's "Read more". */
  linkLabel: Record<AnnouncementLanguage, string> | null;
  startsAt: number;
  endsAt: number;
}

/** What the candidate's browser gets: the text in its language, nothing else. */
export interface LiveAnnouncement {
  id: string;
  text: string;
  link: string | null;
  linkLabel: string | null;
}

/**
 * `internal` is rendered through the router (and so in the reader's language),
 * `external` opens in a new tab. A protocol-relative `//host` is neither — it
 * looks like a path and leaves the site.
 */
export function linkKind(link: string): "internal" | "external" | null {
  if (/^\/(?![/\\])[^\s]*$/.test(link)) return "internal";
  try {
    const url = new URL(link);
    return url.protocol === "https:" && !/\s/.test(link) ? "external" : null;
  } catch {
    return null;
  }
}

export function parseAnnouncement(
  body: unknown,
): { ok: true; value: AnnouncementInput } | { ok: false; error: string } {
  const b = (body ?? {}) as Record<string, unknown>;
  const rawText = (b.text ?? {}) as Record<string, unknown>;
  const text = {} as Record<AnnouncementLanguage, string>;
  for (const lang of ANNOUNCEMENT_LANGUAGES) {
    const value = typeof rawText[lang] === "string" ? (rawText[lang] as string).trim() : "";
    if (!value) return { ok: false, error: `Немає тексту (${lang}).` };
    if (value.length > ANNOUNCEMENT_MAX_CHARS)
      return { ok: false, error: `Текст (${lang}) довший за ${ANNOUNCEMENT_MAX_CHARS} знаків.` };
    text[lang] = value;
  }

  const rawLink = typeof b.link === "string" ? b.link.trim() : "";
  const link = rawLink || null;
  if (link && !linkKind(link))
    return { ok: false, error: "Посилання — шлях у застосунку (/pass) або https://…" };

  // All four or none: a label in one language and "Read more" in the others
  // is the same link saying two different things.
  const rawLabel = (b.linkLabel ?? {}) as Record<string, unknown>;
  const labels = ANNOUNCEMENT_LANGUAGES.map((lang) =>
    typeof rawLabel[lang] === "string" ? (rawLabel[lang] as string).trim() : "",
  );
  let linkLabel: AnnouncementInput["linkLabel"] = null;
  if (labels.some(Boolean)) {
    if (!link) return { ok: false, error: "Підпис є, а посилання немає." };
    if (!labels.every(Boolean)) return { ok: false, error: "Підпис посилання — усіма чотирма мовами або жодною." };
    if (labels.some((l) => l.length > ANNOUNCEMENT_LABEL_MAX_CHARS))
      return { ok: false, error: `Підпис довший за ${ANNOUNCEMENT_LABEL_MAX_CHARS} знаків.` };
    linkLabel = { pl: labels[0]!, uk: labels[1]!, ru: labels[2]!, en: labels[3]! };
  }

  const startsAt = Number(b.startsAt);
  const endsAt = Number(b.endsAt);
  if (!Number.isFinite(startsAt) || !Number.isFinite(endsAt))
    return { ok: false, error: "Вкажи початок і кінець." };
  if (endsAt <= startsAt) return { ok: false, error: "Кінець має бути пізніше за початок." };

  return { ok: true, value: { text, link, linkLabel, startsAt, endsAt } };
}

export const asAnnouncementLanguage = (value: unknown): AnnouncementLanguage =>
  ANNOUNCEMENT_LANGUAGES.includes(value as AnnouncementLanguage)
    ? (value as AnnouncementLanguage)
    : "pl";
